import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { loadYouTubeAPI, YT_VIDEO_ID, TRACK_TITLE, TRACK_ARTIST } from "../youtube";
import { SCENES, SONG_LENGTH, FADE, CREDITS_AT } from "./timeline";
import { VB, FONT, clamp, seg, span } from "./kit";

const fmt = (s) => {
  if (!Number.isFinite(s) || s < 0) s = 0;
  return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

const STYLES = `
  body.wm-on { margin: 0; background: #000; overflow: hidden; }
  .wm-root {
    --ink-3: #2b2b2b;
    --ink-4: #4a4a4a;
    --ink-5: #7a7a7a;
    --ink-6: #b8b8b8;
    --ink-7: #e8e8e8;
    --accent: oklch(58% 0.075 245);
    --serif: "Newsreader", "Times New Roman", serif;
    --mono: "JetBrains Mono", ui-monospace, Menlo, monospace;
    position: fixed; inset: 0;
    background: #000;
    color: var(--ink-6);
    font-family: var(--serif);
    overflow: hidden;
  }
  .wm-root *, .wm-root *::before, .wm-root *::after { box-sizing: border-box; }
  .wm-root .stage { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }
  .wm-root .vignette {
    position: absolute; inset: 0; z-index: 1; pointer-events: none;
    background: radial-gradient(ellipse at center, transparent 58%, rgba(0,0,0,.55) 100%);
  }

  .wm-root .ui { transition: opacity .8s ease; }
  .wm-root.dim .ui { opacity: 0; }
  .wm-root.dim { cursor: none; }

  .wm-root .top {
    position: absolute; top: 0; left: 0; right: 0; z-index: 5;
    display: flex; justify-content: space-between; align-items: center;
    padding: 22px 28px 40px;
    font-family: var(--mono); font-size: 11px;
    letter-spacing: .18em; text-transform: uppercase;
    color: var(--ink-5);
    background: linear-gradient(180deg, rgba(0,0,0,.6), transparent);
  }
  .wm-root .top a { color: var(--ink-5); text-decoration: none; transition: color .2s; }
  .wm-root .top a:hover { color: var(--ink-7); }
  .wm-root .top .act { color: var(--ink-6); }
  .wm-root .top .act b { color: var(--accent); font-weight: 400; margin-right: 10px; }

  .wm-root .chyron {
    position: absolute; left: 28px; top: 72px; z-index: 5;
    pointer-events: none;
  }
  .wm-root .chyron .num {
    font-family: var(--mono); font-size: 11px;
    letter-spacing: .42em; text-transform: uppercase;
    color: var(--ink-5);
  }
  .wm-root .chyron .name {
    font-family: var(--serif); font-style: italic; font-weight: 300;
    font-size: clamp(34px, 4.6vw, 64px); line-height: 1.05;
    color: var(--ink-7);
    text-shadow: 0 2px 30px rgba(0,0,0,.7);
  }

  .wm-root .sync {
    position: absolute; top: 64px; right: 28px; z-index: 7;
    width: 300px; padding: 14px 16px;
    background: rgba(6,6,6,.92); border: 1px solid #1a1a1a;
    font-family: var(--mono); font-size: 10.5px; letter-spacing: .08em;
    color: var(--ink-6);
  }
  .wm-root .sync h3 {
    margin: 0 0 8px; font-size: 10px; font-weight: 400;
    letter-spacing: .3em; text-transform: uppercase; color: var(--ink-5);
  }
  .wm-root .sync .next { color: var(--ink-7); margin-bottom: 10px; line-height: 1.5; }
  .wm-root .sync .next b { color: var(--accent); font-weight: 400; }
  .wm-root .sync .row { display: flex; gap: 10px; padding: 3px 0; color: var(--ink-4); }
  .wm-root .sync .row.done { color: var(--ink-6); }
  .wm-root .sync .row span:first-child { min-width: 28px; }
  .wm-root .sync .row span:nth-child(2) { min-width: 56px; font-variant-numeric: tabular-nums; }
  .wm-root .sync code { display: block; margin-top: 10px; color: var(--ink-7); word-break: break-all; }
  .wm-root .sync .btns { display: flex; gap: 14px; margin-top: 10px; }
  .wm-root .sync button {
    background: none; border: none; padding: 2px 0;
    font: inherit; letter-spacing: .14em; text-transform: uppercase;
    color: var(--accent); cursor: pointer;
  }

  .wm-root .bottom {
    position: absolute; left: 0; right: 0; bottom: 0; z-index: 6;
    display: flex; align-items: center; gap: 18px;
    padding: 40px 28px 22px;
    font-family: var(--mono); font-size: 10.5px;
    letter-spacing: .16em; text-transform: uppercase;
    color: var(--ink-5);
    background: linear-gradient(0deg, rgba(0,0,0,.65), transparent);
  }
  .wm-root .bottom button {
    background: none; border: none; padding: 4px 0;
    font: inherit; letter-spacing: .14em; text-transform: uppercase;
    color: var(--accent); cursor: pointer;
    min-width: 78px; text-align: left;
  }
  .wm-root .rail { position: relative; flex: 1; height: 32px; cursor: pointer; }
  .wm-root .rail .track { position: absolute; left: 0; right: 0; top: 9px; height: 1px; background: var(--ink-3); }
  .wm-root .rail .fill { position: absolute; left: 0; top: 9px; height: 1px; background: var(--ink-6); }
  .wm-root .rail .head {
    position: absolute; top: 9.5px; width: 7px; height: 7px; border-radius: 50%;
    background: var(--ink-7); transform: translate(-50%, -50%);
    box-shadow: 0 0 0 3px #000;
  }
  .wm-root .rail .tick { position: absolute; top: 4px; width: 1px; height: 11px; background: var(--ink-4); }
  .wm-root .rail .lbl {
    position: absolute; top: 19px; padding-left: 6px;
    font-size: 9.5px; letter-spacing: .2em; white-space: nowrap;
    color: var(--ink-4); transition: color .3s;
  }
  .wm-root .rail .lbl.on { color: var(--ink-7); }
  .wm-root .time { font-variant-numeric: tabular-nums; min-width: 92px; text-align: right; }

  .wm-root .credits {
    position: absolute; inset: 0; z-index: 8;
    display: grid; place-items: center; text-align: center;
    padding: 0 24px;
    background: radial-gradient(ellipse at center, rgba(0,0,0,.5), rgba(0,0,0,.88));
  }
  .wm-root .credits .t {
    font-family: var(--serif); font-style: italic; font-weight: 300;
    font-size: clamp(36px, 5vw, 72px); line-height: 1.05;
    color: var(--ink-7);
  }
  .wm-root .credits .a,
  .wm-root .credits .by {
    font-family: var(--mono); text-transform: uppercase;
    color: var(--ink-5);
  }
  .wm-root .credits .a { font-size: 11px; letter-spacing: .42em; margin-top: 16px; }
  .wm-root .credits .by { font-size: 10px; letter-spacing: .24em; margin-top: 26px; color: var(--ink-4); }
  .wm-root .credits .acts { margin-top: 30px; display: flex; gap: 26px; justify-content: center; }
  .wm-root .credits button,
  .wm-root .credits a {
    background: none; border: none; padding: 4px 0; cursor: pointer;
    font-family: var(--mono); font-size: 11px;
    letter-spacing: .18em; text-transform: uppercase;
    color: var(--accent); text-decoration: none;
    border-bottom: 1px solid transparent;
    transition: color .2s, border-color .2s;
  }
  .wm-root .credits button:hover,
  .wm-root .credits a:hover { color: var(--ink-7); border-color: var(--ink-4); }

  .wm-root .gate {
    position: fixed; inset: 0; z-index: 40;
    background: #000;
    display: grid; place-items: center; text-align: center;
    padding: 0 24px;
    transition: opacity .9s ease, visibility 0s linear .9s;
  }
  .wm-root .gate.out { opacity: 0; visibility: hidden; }
  .wm-root .gate .inner { display: flex; flex-direction: column; align-items: center; gap: 18px; }
  .wm-root .gate .t {
    font-family: var(--serif); font-style: italic; font-weight: 300;
    font-size: clamp(34px, 5.4vw, 72px); line-height: 1.05;
    color: var(--ink-6);
  }
  .wm-root .gate .a {
    font-family: var(--mono); font-size: 10.5px;
    letter-spacing: .38em; text-transform: uppercase;
    color: var(--ink-4);
  }
  .wm-root .gate .cmd {
    margin-top: 22px;
    font-family: var(--mono); font-size: 12.5px; letter-spacing: .08em;
    color: var(--ink-6);
    display: flex; align-items: center; gap: 10px;
  }
  .wm-root .gate .cmd .p { color: var(--accent); }
  .wm-root .gate .cmd .touch { display: none; }
  .wm-root .gate .cmd .caret {
    display: inline-block; width: 7px; height: 14px;
    background: var(--ink-6);
    animation: wm-blink 1.05s steps(2) infinite;
    transform: translateY(2px);
  }
  .wm-root .gate .hint {
    font-family: var(--mono); font-size: 10px;
    letter-spacing: .3em; text-transform: uppercase;
    color: var(--ink-3);
  }
  @keyframes wm-blink { 0%,50% { opacity: 1 } 50.01%,100% { opacity: 0 } }
  @media (hover: none) {
    .wm-root .gate .cmd .key { display: none; }
    .wm-root .gate .cmd .touch { display: inline; }
  }

  .wm-root .yt-host {
    position: fixed; right: 0; bottom: 0;
    width: 200px; height: 200px;
    opacity: 0;
    pointer-events: none;
    z-index: -1;
  }
  .wm-root .yt-host iframe { width: 100%; height: 100%; border: 0; }

  @media (max-width: 640px) {
    .wm-root .top { padding: 18px 20px 32px; }
    .wm-root .top .act span { display: none; }
    .wm-root .chyron { left: 20px; top: 60px; }
    .wm-root .sync { right: 20px; width: calc(100% - 40px); }
    .wm-root .bottom { padding: 32px 20px 18px; gap: 12px; }
    .wm-root .bottom button { min-width: 0; }
    .wm-root .rail .lbl .name { display: none; }
    .wm-root .time { min-width: 0; }
  }
`;

// A crashing scene blanks itself instead of the whole page.
class SceneBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <text x={VB.w / 2} y={VB.h / 2} fill="#a33" fontFamily={FONT.mono} fontSize={14} textAnchor="middle">
          {`scene ${this.props.id} crashed: ${this.state.error.message}`}
        </text>
      );
    }
    return this.props.children;
  }
}

export default function WorkingManPage() {
  // ?still=95 freezes the piece at 1:35 with no audio (used for previews);
  // ?t=95 starts playback at 1:35.
  const [still] = useState(() => {
    const v = new URLSearchParams(window.location.search).get("still");
    return v === null ? null : Number(v) || 0;
  });
  const [startAt] = useState(() => Number(new URLSearchParams(window.location.search).get("t")) || 0);
  // ?sync: tap M as each section of the song starts to measure act timings.
  const [sync] = useState(() => new URLSearchParams(window.location.search).has("sync"));
  const [marks, setMarks] = useState([]);
  // Arriving from another page of the site counts as an interaction already,
  // so only a cold visit needs the enter screen.
  const [entered, setEntered] = useState(
    () => still !== null || !!(navigator.userActivation && navigator.userActivation.hasBeenActive)
  );

  const [t, setT] = useState(still !== null ? still : startAt);
  const [duration, setDuration] = useState(SONG_LENGTH);
  const [playing, setPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [idle, setIdle] = useState(false);
  const [wide, setWide] = useState(true);

  const playerRef = useRef(null);
  const hostRef = useRef(null);
  const enteredRef = useRef(entered);
  const sampleRef = useRef({ pt: startAt, at: 0 });
  const tRef = useRef(t);
  tRef.current = t;
  const playingRef = useRef(playing);
  playingRef.current = playing;

  useEffect(() => {
    document.body.classList.add("wm-on");
    const prevTitle = document.title;
    document.title = `${TRACK_TITLE} — joshdlee`;
    return () => {
      document.body.classList.remove("wm-on");
      document.title = prevTitle;
    };
  }, []);

  useEffect(() => {
    const onResize = () => setWide(window.innerWidth / window.innerHeight >= 1.15);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  function start() {
    const p = playerRef.current;
    if (!p || typeof p.playVideo !== "function") return;
    p.unMute();
    p.setVolume(100);
    if (startAt > 0 && (p.getCurrentTime() || 0) < 0.5) p.seekTo(startAt, true);
    p.playVideo();
  }

  function seek(s) {
    const target = clamp(s, 0, Math.max(0, duration - 0.25));
    if (still !== null) {
      setT(target);
      return;
    }
    const p = playerRef.current;
    if (!p || typeof p.seekTo !== "function") return;
    p.seekTo(target, true);
    sampleRef.current = { pt: target, at: performance.now() };
    setT(target);
    if (ended) p.playVideo();
  }

  function toggle() {
    const p = playerRef.current;
    if (!p) return;
    if (playingRef.current) {
      p.pauseVideo();
    } else {
      p.unMute();
      p.playVideo();
    }
  }

  useEffect(() => {
    if (still !== null) return undefined;
    let cancelled = false;
    let player = null;
    const host = hostRef.current;

    // Browsers block audio until the visitor interacts with the page, so the
    // enter screen's first click / tap / keypress is what starts the song.
    const GESTURES = ["pointerdown", "keydown", "touchend"];
    const onFirstGesture = (e) => {
      GESTURES.forEach((g) => window.removeEventListener(g, onFirstGesture, true));
      e.stopPropagation();
      enteredRef.current = true;
      setEntered(true);
      start();
    };
    if (!enteredRef.current) GESTURES.forEach((g) => window.addEventListener(g, onFirstGesture, true));

    loadYouTubeAPI().then((YT) => {
      if (cancelled || !host) return;
      const el = document.createElement("div");
      host.appendChild(el);
      player = new YT.Player(el, {
        videoId: YT_VIDEO_ID,
        playerVars: {
          autoplay: 0,
          controls: 0,
          disablekb: 1,
          fs: 0,
          iv_load_policy: 3,
          playsinline: 1,
          rel: 0,
          origin: window.location.origin,
        },
        events: {
          onReady: (e) => {
            if (cancelled) return;
            playerRef.current = e.target;
            const d = e.target.getDuration();
            if (d) setDuration(d);
            if (enteredRef.current) start();
          },
          onStateChange: (e) => {
            if (e.data === YT.PlayerState.PLAYING) {
              setPlaying(true);
              setEnded(false);
            }
            if (e.data === YT.PlayerState.PAUSED) setPlaying(false);
            if (e.data === YT.PlayerState.ENDED) {
              setPlaying(false);
              setEnded(true);
            }
          },
          onError: (e) => {
            if (!cancelled) setFailed(true);
            console.error("Working Man: youtube error", e.data);
          },
        },
      });
    });

    return () => {
      cancelled = true;
      GESTURES.forEach((g) => window.removeEventListener(g, onFirstGesture, true));
      playerRef.current = null;
      if (player && typeof player.destroy === "function") player.destroy();
      if (host) host.innerHTML = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [still]);

  // The iframe API only reports time every few hundred ms; extrapolate between
  // reports so the animation runs at frame rate.
  useEffect(() => {
    if (still !== null) return undefined;
    let raf = 0;
    const loop = () => {
      const p = playerRef.current;
      if (p && typeof p.getCurrentTime === "function") {
        const now = performance.now();
        const pt = p.getCurrentTime() || 0;
        if (pt !== sampleRef.current.pt) sampleRef.current = { pt, at: now };
        const running = p.getPlayerState() === 1;
        const s = sampleRef.current;
        setT(s.pt + (running ? Math.min(1, (now - s.at) / 1000) : 0));
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [still]);

  useEffect(() => {
    const onKey = (e) => {
      if (!enteredRef.current || still !== null) return;
      if (e.code === "Space") {
        e.preventDefault();
        toggle();
      }
      if (e.key === "ArrowRight") seek(tRef.current + 10);
      if (e.key === "ArrowLeft") seek(tRef.current - 10);
      if (sync && e.key.toLowerCase() === "m") {
        const at = Math.round(tRef.current * 10) / 10;
        setMarks((m) => (m.length < SCENES.length - 1 ? [...m, at] : m));
      }
      if (sync && e.key === "Backspace") setMarks((m) => m.slice(0, -1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  useEffect(() => {
    let timer = null;
    const wake = () => {
      setIdle(false);
      clearTimeout(timer);
      timer = setTimeout(() => setIdle(true), 3000);
    };
    wake();
    const EVENTS = ["pointermove", "pointerdown", "keydown"];
    EVENTS.forEach((ev) => window.addEventListener(ev, wake));
    return () => {
      clearTimeout(timer);
      EVENTS.forEach((ev) => window.removeEventListener(ev, wake));
    };
  }, []);

  // Sync marks override act starts as they're tapped; later acts keep their
  // estimated starts but never start before the act in front of them.
  const acts = [];
  SCENES.forEach((s, i) => {
    let start = i === 0 ? 0 : marks[i - 1] !== undefined ? marks[i - 1] : s.start;
    if (i > 0) start = Math.max(start, acts[i - 1].start + 1);
    acts.push({ ...s, start });
  });
  acts.forEach((a, i) => {
    a.end = i < acts.length - 1 ? acts[i + 1].start : Math.max(a.start + 1, SCENES[i].end);
  });

  const idx = acts.reduce((acc, s, i) => (t >= s.start ? i : acc), 0);
  const act = acts[idx];
  const actLocal = t - act.start;
  const chyron = span(actLocal, 0.4, 1.4, 5.5, 6.5);
  const credits = ended ? 1 : seg(t, CREDITS_AT, CREDITS_AT + 2);
  const progress = clamp(t / duration);

  const onRail = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    seek(((e.clientX - r.left) / r.width) * duration);
  };

  return (
    <div className={`wm-root${idle && playing ? " dim" : ""}`}>
      <style>{STYLES}</style>

      <svg
        className="stage"
        viewBox={`0 0 ${VB.w} ${VB.h}`}
        preserveAspectRatio={wide ? "xMidYMid slice" : "xMidYMid meet"}
        aria-hidden="true"
      >
        {acts.map((s, i) => {
          const last = i === acts.length - 1;
          if (t < s.start - FADE / 2 || (!last && t > s.end + FADE / 2)) return null;
          const dur = s.end - s.start;
          const alpha = i === 0 ? 1 : seg(t, s.start - FADE / 2, s.start + FADE / 2);
          const Scene = s.Component;
          return (
            <g key={s.id} opacity={alpha}>
              <SceneBoundary id={s.id}>
                <Scene p={clamp((t - s.start) / dur)} t={t} local={t - s.start} dur={dur} />
              </SceneBoundary>
            </g>
          );
        })}
      </svg>
      <div className="vignette"></div>

      <header className="top ui">
        <Link to="/index">&larr; jdl &nbsp;/&nbsp; index</Link>
        <div className="act">
          <b>{act.n}</b>
          <span>{act.title} &nbsp;&middot;&nbsp; </span>
          {fmt(t)}
        </div>
      </header>

      {chyron > 0 && (
        <div className="chyron" style={{ opacity: chyron }}>
          <div className="num">Act {act.n}</div>
          <div className="name">{act.title}</div>
        </div>
      )}

      {sync && (
        <aside className="sync">
          <h3>Sync mode</h3>
          <div className="next">
            {marks.length < SCENES.length - 1 ? (
              <>Tap <b>M</b> when the {SCENES[marks.length + 1].cue} starts</>
            ) : (
              <>Done. Send these act starts to Claude, or paste them into timeline.js:</>
            )}
          </div>
          {acts.map((a, i) => (
            <div key={a.id} className={`row${i === 0 || marks[i - 1] !== undefined ? " done" : ""}`}>
              <span>{a.n}</span>
              <span>{i === 0 || marks[i - 1] !== undefined ? fmt(a.start) + "." + Math.floor((a.start % 1) * 10) : "\u2014"}</span>
              <span>{a.cue}</span>
            </div>
          ))}
          <code>[{acts.map((a) => a.start).join(", ")}]</code>
          <div className="btns">
            <button onClick={() => setMarks((m) => m.slice(0, -1))}>Undo</button>
            <button onClick={() => setMarks([])}>Reset</button>
            <button onClick={() => navigator.clipboard && navigator.clipboard.writeText(`[${acts.map((a) => a.start).join(", ")}]`)}>Copy</button>
          </div>
        </aside>
      )}

      <footer className="bottom ui">
        <button onClick={toggle} disabled={still !== null}>
          {playing ? "■ Pause" : "▶ Play"}
        </button>
        <div className="rail" onClick={onRail}>
          <div className="track"></div>
          <div className="fill" style={{ width: `${progress * 100}%` }}></div>
          {acts.map((s, i) => (
            <React.Fragment key={s.id}>
              <div className="tick" style={{ left: `${(s.start / duration) * 100}%` }}></div>
              <div className={`lbl${i === idx ? " on" : ""}`} style={{ left: `${(s.start / duration) * 100}%` }}>
                {s.n}<span className="name"> {s.title}</span>
              </div>
            </React.Fragment>
          ))}
          <div className="head" style={{ left: `${progress * 100}%` }}></div>
        </div>
        <div className="time">{fmt(t)} / {fmt(duration)}</div>
      </footer>

      {credits > 0 && (
        <div className="credits" style={{ opacity: credits, pointerEvents: credits > 0.5 ? "auto" : "none" }}>
          <div>
            <div className="t">{TRACK_TITLE}</div>
            <div className="a">{TRACK_ARTIST}</div>
            <div className="by">A visual in six acts &middot; joshdlee</div>
            <div className="acts">
              <button onClick={() => seek(0)}>&#8634; Replay</button>
              <Link to="/index">&larr; joshdlee</Link>
            </div>
          </div>
        </div>
      )}

      {still === null && (
        <div className={`gate${entered ? " out" : ""}`} aria-hidden={entered}>
          <div className="inner">
            <div className="t">{TRACK_TITLE}</div>
            <div className="a">{TRACK_ARTIST} &middot; a visual in six acts</div>
            <div className="cmd">
              <span className="p">~ $</span>
              <span>
                <span className="key">{failed ? "player unavailable" : "press any key to begin"}</span>
                <span className="touch">{failed ? "player unavailable" : "tap to begin"}</span>
              </span>
              <span className="caret"></span>
            </div>
            <div className="hint">{wide ? "Sound on" : "Sound on · turn your phone sideways"}</div>
          </div>
        </div>
      )}

      <div className="yt-host" ref={hostRef} aria-hidden="true"></div>
    </div>
  );
}
